import React from 'react';
import { useRecordContext } from 'react-admin';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Box
} from '@mui/material';

const OperationPlanMultiField = ({ source = 'selected_plans', field_meta }) => {
  const record = useRecordContext();
  const selectedPlans = record?.[source] || [];

  // Default editable fields structure (same as input component)
  const defaultEditableFields = [
    { 
      field: 'exterior_structure_receive', 
      label: 'الهيكل الخارجي (استلام)', 
      type: 'select',
      category: 'receive'
    },
    { 
      field: 'exterior_structure_deliver', 
      label: 'الهيكل الخارجي (تسليم)', 
      type: 'select',
      category: 'deliver'
    },
    { 
      field: 'tires_receive', 
      label: 'الإطارات (استلام)', 
      type: 'select',
      category: 'receive'
    },
    { 
      field: 'tires_deliver', 
      label: 'الإطارات (تسليم)', 
      type: 'select',
      category: 'deliver'
    },
    { 
      field: 'lights_receive', 
      label: 'الأنوار الأمامية / الخلفية (استلام)', 
      type: 'select',
      category: 'receive'
    },
    { 
      field: 'lights_deliver', 
      label: 'الأنوار الأمامية / الخلفية (تسليم)', 
      type: 'select',
      category: 'deliver'
    },
    { 
      field: 'fire_extinguisher_receive', 
      label: 'طفاية الحريق (استلام)', 
      type: 'select',
      category: 'receive'
    },
    { 
      field: 'fire_extinguisher_deliver', 
      label: 'طفاية الحريق (تسليم)', 
      type: 'select',
      category: 'deliver'
    },
    { 
      field: 'first_aid_receive', 
      label: 'أدوات الإسعافات الأولية (استلام)', 
      type: 'select',
      category: 'receive'
    },
    { 
      field: 'first_aid_deliver', 
      label: 'أدوات الإسعافات الأولية (تسليم)', 
      type: 'select',
      category: 'deliver'
    },
    { 
      field: 'fuel_receive', 
      label: 'الوقود الكمية التقريبية (استلام)', 
      type: 'select',
      category: 'receive'
    },
    { 
      field: 'fuel_deliver', 
      label: 'الوقود الكمية التقريبية (تسليم)', 
      type: 'select',
      category: 'deliver'
    },
    { 
      field: 'cleanliness_receive', 
      label: 'نظافة الحافلة (استلام)', 
      type: 'select',
      category: 'receive'
    },
    { 
      field: 'cleanliness_deliver', 
      label: 'نظافة الحافلة (تسليم)', 
      type: 'select',
      category: 'deliver'
    },
    { 
      field: 'ac_receive', 
      label: 'أجهزة التكييف (استلام)', 
      type: 'select',
      category: 'receive'
    },
    { 
      field: 'ac_deliver', 
      label: 'أجهزة التكييف (تسليم)', 
      type: 'select',
      category: 'deliver'
    },
    { 
      field: 'notes_receive', 
      label: 'الملاحظات (استلام)', 
      type: 'text',
      category: 'receive'
    },
    { 
      field: 'notes_deliver', 
      label: 'الملاحظات (تسليم)', 
      type: 'text',
      category: 'deliver'
    },
  ];

  // Parse editable fields from field_meta or use default
  const editableFields = React.useMemo(() => {
    let fields = defaultEditableFields;
    
    if (field_meta && field_meta.editable_fields) {
      try {
        fields = typeof field_meta.editable_fields === 'string' 
          ? JSON.parse(field_meta.editable_fields) 
          : field_meta.editable_fields;
      } catch (e) {
        console.error('Error parsing editable_fields from field_meta:', e);
      }
    }
    
    return fields;
  }, [field_meta]);

  // Group fields by base field name (exterior_structure, tires, etc.)
  const groupedFields = React.useMemo(() => {
    const groups = [];
    const processed = new Set();
    
    editableFields.forEach(field => {
      // Extract base field name (remove _receive or _deliver)
      const baseName = field.field.replace(/_receive$|_deliver$/, '');
      
      if (!processed.has(baseName)) {
        processed.add(baseName);
        
        // Find both receive and deliver fields for this base name
        const receiveField = editableFields.find(f => f.field === `${baseName}_receive`);
        const deliverField = editableFields.find(f => f.field === `${baseName}_deliver`);
        
        if (receiveField && deliverField) {
          groups.push({
            baseName,
            label: receiveField.label.replace(' (استلام)', '').replace(' (تسليم)', ''),
            receiveField,
            deliverField
          });
        }
      }
    });
    
    return groups;
  }, [editableFields]);

  if (!selectedPlans || selectedPlans.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: 'gray' }}>
        لا توجد حافلات محددة
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        الحافلات المحددة ({selectedPlans.length})
      </Typography>
      
      <Box sx={{ width: '95vw', maxWidth: '95vw' }}>
        <TableContainer component={Paper} sx={{ maxHeight: '600px', overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                  الرقم التشغيلي
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                  اللوحة
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                  المدرسة
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                  السائق
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                  رقم الهوية
                </TableCell>
                {groupedFields.map((group) => (
                  <TableCell
                    key={group.baseName}
                    sx={{
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      minWidth: '200px',
                      fontWeight: 'bold',
                    }}
                  >
                    {group.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedPlans.map((plan, index) => (
                <TableRow key={plan.id || index}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {plan.operationnumber || '--'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {`${plan.platenumbers || ''} ${plan.plateletters || ''}`}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {plan.scname || '--'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {plan.name || '--'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {plan.nid_number || '--'}
                  </TableCell>
                  {groupedFields.map((group) => (
                    <TableCell key={group.baseName}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Receive Field - Read Only */}
                        <Box>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block', 
                              mb: 0.5, 
                              fontWeight: 'bold', 
                              color: '#1976d2' 
                            }}
                          >
                            استلام
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              p: 1, 
                              bgcolor: '#e3f2fd', 
                              borderRadius: 1, 
                              minHeight: '40px',
                              border: '1px solid #90caf9'
                            }}
                          >
                            {plan[group.receiveField.field] || '--'}
                          </Typography>
                        </Box>
                        
                        {/* Deliver Field - Read Only */}
                        <Box>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block', 
                              mb: 0.5, 
                              fontWeight: 'bold', 
                              color: '#d32f2f' 
                            }}
                          >
                            تسليم
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              p: 1, 
                              bgcolor: '#ffebee', 
                              borderRadius: 1, 
                              minHeight: '40px',
                              border: '1px solid #ef9a9a'
                            }}
                          >
                            {plan[group.deliverField.field] || '--'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default OperationPlanMultiField;
